<script setup lang="ts">
import 'jquery-validation';

import $ from 'jquery';
import {
    inject, nextTick, onBeforeUnmount, onMounted, provide, shallowRef,
    unref,
    watch,
} from 'vue';

import { prepareCustomValidationMethods, setupAutofillControls, setupAutoprettifyControls } from '#public/commons-global';
import { BaseIgnoreList, type ComponentState, useValidator } from '#public_ts/composables';

import { PortalPin, ValidatorInstance } from './injection';
import type { AdditionalValidatorOptions, ValidateFormApi } from './validate-form';


/*
Name attribute must be present to validate.
May process built-in attributes as rules:
- required: <InputBox required />
- min <InputBox min="0" />
- max
- minlength
- maxlength
- step
Input types as rules:
- type="email"
- type="number"
- type="url"
- type="date"
- type="range"
May process data attributes as rules (for simple formats):
- data-rule-rangelength="[1, 255]"
- :data-rule-equalto="`#${passwordId}`"
Special jquery data properties (for complex formats):
- :data="{ rules: { IVAorCF: { paese: `#${ids.id_paese}` } } }"
- :data="{ rule-ivaorcf: { paese: `#${ids.id_paese}` } }"
- :data="{ ruleIvaorcf: { paese: `#${ids.id_paese}` } }"
Note: jquery accepts either camelcase or dashed lower case for data attributes, don't include dash into your rule names.
Note2: data-attributes.ts will convert to lower case
External rules object for complex forms:
- <ValidateForm :validator="{ rules }" />
*/

// pin form if it is inside dialog, so it will not be teleported away from dialog
// make form standAlone if it is not inside dialog and doesn't contain inner dialog
// so all inner dialogs will not stay inside form and be teleported away
const props = defineProps<{
    validator?: AdditionalValidatorOptions;
    state?: ComponentState;
    pin?: boolean;
    formClass?: string;
    ignoreReadonly?: boolean;
    rawSubmit?: boolean;
    autoFill?: boolean;
    standAlone?: boolean;
}>();
const emit = defineEmits<{
    (event: 'form-submit', value: undefined): void;
}>();

const formElement = shallowRef<HTMLFormElement | undefined>();
const validatorInstance = shallowRef<JQueryValidation.Validator | undefined>();
//pin both dialog and this form according to props, otherwise pin just dialog and portal this form
const portalPin = inject(PortalPin, undefined);
if((portalPin === undefined) && !props.standAlone) {
    provide(PortalPin, true);
}
//this is for child input controls
provide(ValidatorInstance, validatorInstance);

const publicApi: ValidateFormApi = {
    formElement,
    validatorInstance,
    validateForm() {
        const _validator = unref(validatorInstance);
        if(_validator) {
            return _validator.form();
        }
        return true;
    },
    resetForm(ids?: string[]) {
        const _validator = unref(validatorInstance) as (JQueryValidation.Validator & { resetElements: (elements: JQuery) => void }) | undefined;
        const _formElement = unref(formElement);
        if(_validator && _formElement) {
            if(!ids || !ids.length) {
                //first reset ignored elements, because validator will not reset ignored elements
                //and invalid may become ignored (may become readonly)
                const ignored = $(_formElement)
                    .find('input, select, textarea, [contenteditable]')
                    .filter(`.no-validate,:disabled${props.ignoreReadonly ? ',[readonly]' : ''}`);
                _validator.resetElements(ignored);
                _validator.resetForm();
            } else {
                const nodes = $.map(ids, (i) => document.getElementById(i));
                _validator.resetElements($(nodes));
                $(nodes).removeAttr('aria-invalid');
            }
        }
    },
    reset() {
        const _formElement = unref(formElement);
        if(_formElement) {
            _formElement.reset();
            publicApi.resetForm();
        }
    },
};
defineExpose(publicApi);

function onRawSubmit(event: Event) {
    if(props.rawSubmit) {
        event.preventDefault();
        emit('form-submit', undefined);
    }
}

const onUpdate = props.state?.onUpdate(() => {
    const _validator = unref(validatorInstance);
    if(_validator) {
        _validator.resetForm();
        //this will not reset ignored elements (which became readonly)
    }
});

function updateValidatorInstance() {
    const _formElement = unref(formElement);
    const _validator = unref(validatorInstance);
    if(_validator) {
        _validator.destroy();
        validatorInstance.value = undefined;
    }
    if(_formElement) {
        const submitHandler: JQueryValidation.ValidationOptions['submitHandler'] = props.rawSubmit
            ? undefined
            : function(form, event) {
                event?.preventDefault();
                emit('form-submit', undefined);
            };
        let validatorOptions: AdditionalValidatorOptions = {
            submitHandler,
            ignore: (props.ignoreReadonly || props.validator?.ignoreReadonly) ? `${BaseIgnoreList}[readonly]` : BaseIgnoreList,
            ...(props.validator ?? {}),
        };
        if(validatorOptions.useValidatorDefaults !== false) {
            validatorOptions = useValidator(validatorOptions);
        }
        const _validatorInstance = $(_formElement).validate(validatorOptions);
        validatorInstance.value = _validatorInstance;
    }
}

watch([
    () => props.validator,
    () => props.ignoreReadonly,
    () => props.rawSubmit,
], updateValidatorInstance);

onMounted(() => {
    //portal requires next tick
    nextTick(() => {
        const _formElement = unref(formElement);
        if(_formElement) {
            prepareCustomValidationMethods();
            setupAutoprettifyControls($(_formElement));
        }

        updateValidatorInstance();

        const _validatorInstance = unref(validatorInstance);
        if(_validatorInstance && props.autoFill) {
            setupAutofillControls(_validatorInstance, _formElement);
        }
    });
});

onBeforeUnmount(() => {
    const _validator = unref(validatorInstance);
    if(_validator) {
        _validator.destroy();
    }
    onUpdate?.off();
});
</script>
<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
<template>
    <Teleport to="#portal-target" :disabled="props.pin || props.standAlone || portalPin">
        <form v-bind="$attrs" ref="formElement" :class="props.formClass" @submit="onRawSubmit">
            <slot />
        </form>
    </Teleport>
</template>
